import { Injectable } from '@angular/core'
import { CookieService } from 'ngx-cookie-service'
import { environment } from '../../../../environments/environment'
import {
  PublicClientApplication,
  Configuration,
  AuthenticationResult,
  SilentRequest,
  AccountInfo,
  BrowserAuthError,
  InteractionRequiredAuthError
} from '@azure/msal-browser'

@Injectable({
  providedIn: 'root'
})
export class CognitoService {
  private msalInstance: PublicClientApplication
  private initPromise: Promise<void> | null = null
  private redirectInProgress = false

  constructor (
    public cookieService: CookieService
  ) {
    const msalConfig: Configuration = {
      auth: {
        clientId: environment.azureAd.clientId,
        authority: environment.azureAd.authority,
        redirectUri: environment.azureAd.redirectUri,
        postLogoutRedirectUri: environment.azureAd.postLogoutRedirectUri,
        navigateToLoginRequestUrl: true
      },
      cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: false
      }
    }
    this.msalInstance = new PublicClientApplication(msalConfig)
  }

  /**
   * Initializes MSAL and handles any pending redirect response.
   * Uses a singleton promise so that multiple callers (AppComponent,
   * CounsellingModeComponent, AuthRedirectComponent) all wait for the
   * same initialization — preventing race conditions where loginRedirect
   * fires before handleRedirectPromise completes.
   */
  async initialize (): Promise<void> {
    if (!this.initPromise) {
      this.initPromise = this.doInitialize()
    }
    return await this.initPromise
  }

  private async doInitialize (): Promise<void> {
    await this.msalInstance.initialize()

    // handleRedirectPromise MUST be called exactly once per page load.
    // It resolves with the auth response if we're returning from Azure AD,
    // or null if this is a fresh navigation.
    try {
      const response = await this.msalInstance.handleRedirectPromise()
      if (response) {
        this.msalInstance.setActiveAccount(response.account)
        this.storeTokensInCookies(response)
      } else {
        // No redirect response — set active account from cache if available
        const accounts = this.msalInstance.getAllAccounts()
        if (accounts.length > 0) {
          this.msalInstance.setActiveAccount(accounts[0])
        }
      }
    } catch (error) {
      console.warn('MSAL handleRedirectPromise failed:', error)
      // Clear stale interaction state so next login attempt works
      this.clearMsalInteractionState()
    }
  }

  /**
   * Acquires a token silently using the cached account.
   * If silent acquisition fails, triggers an interactive redirect.
   */
  async getAccessToken (): Promise<void> {
    await this.initialize()

    const account = this.getAccount()
    if (!account) {
      await this.loginRedirect()
      return
    }

    const silentRequest: SilentRequest = {
      scopes: environment.azureAd.scopes,
      account
    }

    try {
      const response = await this.msalInstance.acquireTokenSilent(silentRequest)
      this.storeTokensInCookies(response)
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError || error instanceof BrowserAuthError) {
        console.warn('Silent token acquisition requires interaction:', error)
        await this.loginRedirect()
      } else {
        console.error('Unexpected error during silent token acquisition:', error)
        await this.loginRedirect()
      }
    }
  }

  /**
   * Redirects the user to Azure AD login page.
   * Guards against multiple simultaneous redirects and clears stale state.
   */
  async loginRedirect (): Promise<void> {
    if (this.redirectInProgress) {
      return
    }
    this.redirectInProgress = true

    await this.initialize()

    // Clear stale interaction state from any previously interrupted login
    this.clearMsalInteractionState()

    try {
      await this.msalInstance.loginRedirect({
        scopes: environment.azureAd.scopes,
        prompt: 'select_account'
      })
    } catch (error) {
      this.redirectInProgress = false
      console.error('loginRedirect failed:', error)
      // Clear all MSAL cache and retry once
      this.clearMsalInteractionState()
      try {
        await this.msalInstance.loginRedirect({
          scopes: environment.azureAd.scopes,
          prompt: 'select_account'
        })
      } catch (retryError) {
        this.redirectInProgress = false
        console.error('loginRedirect retry failed:', retryError)
        // As a last resort, do a full page redirect to the authority login
        window.location.href = `${environment.azureAd.authority}/oauth2/v2.0/authorize?client_id=${environment.azureAd.clientId}&response_type=code&redirect_uri=${encodeURIComponent(environment.azureAd.redirectUri)}&scope=${encodeURIComponent(environment.azureAd.scopes.join(' '))}&prompt=select_account`
      }
    }
  }

  /**
   * Logs the user out and clears cookies.
   */
  async logout (): Promise<void> {
    await this.initialize()
    this.cookieService.delete('wircognito-access-token')
    this.cookieService.delete('wircognito-new-access-token')
    this.cookieService.delete('wircognito-refresh')
    await this.msalInstance.logoutRedirect({
      postLogoutRedirectUri: environment.azureAd.postLogoutRedirectUri
    })
  }

  /**
   * Checks if the user is currently authenticated.
   */
  isAuthenticated (): boolean {
    return this.getAccount() !== null
  }

  /**
   * Gets the active account from MSAL cache.
   */
  private getAccount (): AccountInfo | null {
    return this.msalInstance.getActiveAccount() || this.msalInstance.getAllAccounts()[0] || null
  }

  /**
   * Clears MSAL's interaction-in-progress entries from localStorage.
   * Stale entries cause "We couldn't sign you in" on the Microsoft picker
   * when a previous login was interrupted.
   */
  private clearMsalInteractionState (): void {
    const keysToRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && (
        key.includes('msal.interaction') ||
        (key.includes('msal.') && key.includes('.request.'))
      )) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key))
  }

  /**
   * Stores tokens in cookies for backward compatibility with the backend API.
   */
  private storeTokensInCookies (response: AuthenticationResult): void {
    const authToken = response.idToken || response.accessToken
    if (authToken) {
      this.cookieService.set('wircognito-new-access-token', authToken, undefined, '/')
      this.cookieService.set('wircognito-access-token', authToken, undefined, '/')
    }
    // Backend expects a Refresh-Token header. Azure AD doesn't expose refresh tokens
    // to the browser, so we send the idToken as a stand-in.
    if (response.idToken) {
      this.cookieService.set('wircognito-refresh', response.idToken, undefined, '/')
    }
  }
}
